import React, { useEffect, useState } from "react";
import {
  getCylinders,
  deleteCylinder,
  createCylinder,
  updateCylinder,
} from "../api/cylinderMasterApi";
import { getAllGoods } from "../api/goodsApi";
import "../style/CylinderMaster.css";
import CylinderModal from "./CylinderModal";
import { FiFilter, FiEdit, FiTrash2 } from "react-icons/fi";

const CylinderMaster = () => {
  const [cylinders, setCylinders] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    cylinderName: "",
    batchNo: "",
    srno: "",
    manufacturingDate: "",
    testingDate: "",
    cylinderCapacity: "",
  });

  const [searchFilters, setSearchFilters] = useState({
    cylinderName: "",
    srno: "",
    cylinderCapacity: "",
    fromManufacturingDate: "",
    toManufacturingDate: "",
    testingDate: "",
  });

  const [goods, setGoods] = useState([]);

  // ────────────────────────────────────────────────
  // Fetch cylinders (runs when page or filters change)
  // ────────────────────────────────────────────────
  const fetchCylinders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size,
      };

      // Only add filters that have values
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });

      const response = await getCylinders(params);
      const data = response.data || response; // Handle both axios and fetch responses

      setCylinders(data.content || []);
      setTotalPages(data.totalPages || 0);
      setPage(data.number || 0);
    } catch (error) {
      console.error("Error fetching cylinders:", error);
      setCylinders([]);
    } finally {
      setLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  // Fetch goods data
  // ────────────────────────────────────────────────
  const fetchGoods = async () => {
    try {
      const response = await getAllGoods();
      const goodsData = response.data?.content || response.data || [];
      setGoods(Array.isArray(goodsData) ? goodsData : []);
    } catch (error) {
      console.error("Error fetching goods:", error);
      setGoods([]);
    }
  };

  useEffect(() => {
    fetchCylinders();
    fetchGoods();
  }, [page, searchFilters]); // Important: re-fetch on filter change too

  // ────────────────────────────────────────────────
  // Reset filters
  // ────────────────────────────────────────────────
  const clearSearchFilters = () => {
    setSearchFilters({
      cylinderName: "",
      srno: "",
      cylinderCapacity: "",
      fromManufacturingDate: "",
      toManufacturingDate: "",
      testingDate: "",
    });
    setPage(0);
  };

  // ────────────────────────────────────────────────
  // Search / Apply filters
  // ────────────────────────────────────────────────
  const handleApplyFilters = () => {
    setPage(0); // reset to first page when filters change
    // fetchCylinders() will be triggered by useEffect because searchFilters changed
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ────────────────────────────────────────────────
  // CRUD – Delete
  // ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this cylinder?")) return;

    try {
      await deleteCylinder(id);
      fetchCylinders();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete cylinder.");
    }
  };

  // ────────────────────────────────────────────────
  // CRUD – Edit / Create
  // ────────────────────────────────────────────────
  const handleOpenModalForEdit = (cylinder) => {
    setFormData({
      cylinderName: cylinder.cylinderName || "",
      batchNo: cylinder.batchNo || "",
      srno: cylinder.srno || "",
      manufacturingDate: cylinder.manufacturingDate || "",
      testingDate: cylinder.testingDate || "",
      cylinderCapacity: cylinder.cylinderCapacity || "",
    });
    setEditingId(cylinder.id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleOpenModalForCreate = () => {
    setFormData({
      cylinderName: "",
      batchNo: "",
      srno: "",
      manufacturingDate: "",
      testingDate: "",
      cylinderCapacity: "",
    });
    setIsEditing(false);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleSaveCylinder = async () => {
    try {
      if (isEditing) {
        await updateCylinder(editingId, formData);
      } else {
        await createCylinder(formData);
      }

      setIsModalOpen(false);
      fetchCylinders();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save cylinder. Please check the data.");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingId(null);
  };

  // ────────────────────────────────────────────────
  // Helper function to get goods details by name
  // ────────────────────────────────────────────────
  const getGoodsDetails = (cylinderName) => {
    if (!cylinderName) return null;
    return goods.find(good => good.name === cylinderName);
  };

  // ────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────
  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '100vw',
      overflowX: 'hidden',
      minHeight: '100vh',
      boxSizing: 'border-box'
    }}>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          flex: '1', 
          minWidth: '200px',
          overflow: 'hidden'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>Cylinder Master</h2>
        </div>

        <div style={{ 
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            title={showFilters ? "Hide Filters" : "Show Filters"}
            style={{ 
              padding: '10px 20px',
              border: `2px solid ${showFilters ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: '8px',
              backgroundColor: showFilters ? '#3b82f6' : '#ffffff',
              color: showFilters ? '#ffffff' : '#475569',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: showFilters 
                ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                : '0 2px 4px rgba(0, 0, 0, 0.1)',
              transform: showFilters ? 'translateY(-1px)' : 'translateY(0)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!showFilters) {
                e.target.style.backgroundColor = '#f8fafc';
                e.target.style.borderColor = '#3b82f6';
                e.target.style.color = '#3b82f6';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showFilters) {
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#475569';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseDown={(e) => {
              e.target.style.transform = showFilters ? 'translateY(0)' : 'translateY(-2px)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = showFilters ? 'translateY(-1px)' : 'translateY(-1px)';
            }}
          >
            <FiFilter size={18} />
            {/* {showFilters ? 'Hide Filters' : 'Show Filters'} */}
          </button>

          <button 
            onClick={handleOpenModalForCreate}
            style={{ 
              padding: '10px 20px',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.borderColor = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3b82f6';
              e.target.style.borderColor = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.3)';
            }}
            onMouseDown={(e) => {
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = 'translateY(-1px)';
            }}
          >
            + Add Cylinder
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filter-card">
          <div className="filter-grid">

            <div className="filter-field">
              <label>Cylinder Name</label>
              <input
                type="text"
                placeholder="Enter name..."
                value={searchFilters.cylinderName}
                onChange={(e) => handleSearchChange("cylinderName", e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label>Serial Number</label>
              <input
                type="text"
                placeholder="SR no..."
                value={searchFilters.srno}
                onChange={(e) => handleSearchChange("srno", e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label>Capacity</label>
              <input
                type="text"
                placeholder="e.g. 14.2 kg"
                value={searchFilters.cylinderCapacity}
                onChange={(e) => handleSearchChange("cylinderCapacity", e.target.value)}
              />
            </div>

            <div className="filter-group date-range">
              <div className="filter-field">
                <label>Manufactured From</label>
                <input
                  type="date"
                  value={searchFilters.fromManufacturingDate}
                  onChange={(e) => handleSearchChange("fromManufacturingDate", e.target.value)}
                />
              </div>

              <div className="filter-field">
                <label>To</label>
                <input
                  type="date"
                  value={searchFilters.toManufacturingDate}
                  onChange={(e) => handleSearchChange("toManufacturingDate", e.target.value)}
                />
              </div>
            </div>

            <div className="filter-field">
              <label>Tested On</label>
              <input
                type="date"
                value={searchFilters.testingDate}
                onChange={(e) => handleSearchChange("testingDate", e.target.value)}
              />
            </div>

            <div className="filter-actions">
              <button className="filter-btn reset-btn" onClick={clearSearchFilters}>
                Reset
              </button>
              <button className="btn btn-primary" onClick={handleApplyFilters}>
                Apply
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        overflowX: 'auto',
        overflowY: 'hidden',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        marginBottom: '20px'
      }}>
        <div style={{ 
          minWidth: '800px',
          width: '100%',
          maxWidth: '100%'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
              Loading cylinders...
            </div>
          ) : cylinders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
              No cylinders found
            </div>
          ) : (
            <table style={{ 
              width: '100%',
              minWidth: '800px',
              borderCollapse: 'collapse',
              fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              tableLayout: 'auto'
            }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '150px'
                }}>Cylinder Name</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '100px'
                }}>Batch No</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '100px'
                }}>SR No</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '120px'
                }}>Mfg Date</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '120px'
                }}>Testing Date</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '80px'
                }}>Capacity</th>
                <th style={{ 
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.875rem',
                  borderBottom: '2px solid #e2e8f0',
                  minWidth: '120px'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cylinders.map((cylinder) => {
                const goodsDetails = getGoodsDetails(cylinder.cylinderName);
                return (
                  <tr 
                    key={cylinder.id}
                    style={{ 
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <td style={{ 
                      padding: '12px 16px',
                      verticalAlign: 'middle',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                          {cylinder.cylinderName || "—"}
                        </div>
                        {goodsDetails && (
                          <div style={{ fontSize: '0.75em', color: '#6b7280' }}>
                            ID: {goodsDetails.id}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: '#4b5563'
                    }}>
                      {cylinder.batchNo || "—"}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: '#4b5563'
                    }}>
                      {cylinder.srno || "—"}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: '#4b5563'
                    }}>
                      {cylinder.manufacturingDate || "—"}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: '#4b5563'
                    }}>
                      {cylinder.testingDate || "—"}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      color: '#4b5563'
                    }}>
                      {cylinder.cylinderCapacity || "—"}
                    </td>
                    <td style={{ 
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <button
                          type="button"
                          onClick={() => handleOpenModalForEdit(cylinder)}
                          title="Edit"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#2563eb';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#3b82f6';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          <FiEdit size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(cylinder.id)}
                          title="Delete"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dc2626';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#ef4444';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </button>

          <span className="page-info">
            Page {page + 1} of {totalPages}
          </span>

          <button
            className="page-btn"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      <CylinderModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSaveCylinder}
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
      />

    </div>
  );
};

export default CylinderMaster;